'use client';
import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Box, TextField, CircularProgress, Stack, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PickerField from '../../../components/PickerField';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { getUserById, createUser, updateUser } from '../../../stores/masterUserStore';
import { useCompanyDataList } from '../../../stores/companyStore';
import { useRoleList } from '../../../stores/roleStore';
import usePickerPagination from '../../../hooks/usePickerPagination';
import { useLanguage } from '../../../utils/i18n';
import { useNotifier } from '../../../components/Notifier';

const MasterUserAddEdit = forwardRef(
  (
    { id, onClose, onSuccess, onError, companyOptions: companyOptionsFromParent = [], onSubmitStart = () => {}, onSubmitEnd = () => {} },
    ref
  ) => {
    const { t } = useLanguage();
    const notify = useNotifier();
    const [initialValues, setInitialValues] = useState({
      id: null,
      fullname: '',
      username: '',
      password: '',
      email: '',
      company: null,
      company_name: '',
      role: null,
      role_name: ''
    });

    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const formikRef = useRef(null);

    const companyPickerParams = usePickerPagination({
      fetchHook: useCompanyDataList,
      buildArgs: (state) => [state.page, state.pageSize, 'name', state.searchValue],
      searchKey: 'name'
    });

    const companyOptions =
      companyOptionsFromParent && companyOptionsFromParent.length ? companyOptionsFromParent : companyPickerParams.options;

    useImperativeHandle(ref, () => ({ submitForm: () => formikRef.current?.submitForm?.() }));
    // --- 1. STATE UNTUK PAGINATION & SEARCH DINAMIS (ROLE) ---
    const rolePickerParams = usePickerPagination({
      fetchHook: useRoleList,
      buildArgs: (state) => [state.page, state.pageSize, 'all', state.searchValue],
      formatOption: (r) => {
        const rawId = r.role_name ?? '';
        const value = rawId === null || rawId === undefined ? '' : String(rawId);
        const label = r.role_name ?? String(value);
        return { value, label };
      }
    });

    const roleOptions = rolePickerParams.options;

    // --- 2. AUTO-FILL COMPANY DARI LOCAL STORAGE (KHUSUS ADD MODE) ---
    useEffect(() => {
      if (id) return; // Mencegah penimpaan data jika sedang mode Edit

      try {
        const storedCompanyId = localStorage.getItem('companyId');
        if (!storedCompanyId) return;
        if (!companyPickerParams.options || companyPickerParams.options.length === 0) return;

        const matched = companyPickerParams.options.find((opt) => String(opt.value ?? opt.id ?? opt) === String(storedCompanyId));
        const valueToSet = matched ? (matched.value ?? matched.id ?? storedCompanyId) : storedCompanyId;

        const getLabel = (opt) => opt?.label ?? opt?.name ?? String(opt?.value ?? opt?.id ?? storedCompanyId);

        setInitialValues((prev) => ({
          ...prev,
          company: valueToSet,
          company_name: matched ? getLabel(matched) : String(valueToSet)
        }));

        if (formikRef.current && typeof formikRef.current.setFieldValue === 'function') {
          formikRef.current.setFieldValue('company', valueToSet);
          formikRef.current.setFieldValue('company_name', matched ? getLabel(matched) : String(valueToSet));
        }
      } catch (err) {
        console.log(err);
      }
    }, [companyPickerParams.options, id]);

    useEffect(() => {
      let mounted = true;
      const fetch = async () => {
        if (!id) return;
        setLoading(true);
        try {
          const data = await getUserById(id);
          if (!mounted) return;
          if (data) {
            const fullnameVal = data.fullname ?? data.name ?? data.username ?? '';
            const usernameVal = data.username ?? data.user ?? '';
            const passwordVal = data.password ?? '';
            const emailVal = data.email ?? '';
            const companyVal = data.company ?? data.company_id ?? data.companyId ?? null;
            const companyNameVal = data.join?.company_name ?? data.company_name ?? data.companyName ?? '';
            const roleVal = data.role ?? data.role_id ?? data.roleId ?? data.role_id ?? null;
            const roleNameVal = data.role_name ?? data.roleName ?? '';
            setInitialValues((prev) => ({
              ...prev,
              id: data.id ?? prev.id,
              fullname: fullnameVal,
              username: usernameVal,
              password: passwordVal,
              email: emailVal,
              company: companyVal,
              company_name: companyNameVal,
              role: roleVal === null || roleVal === undefined ? prev.role : String(roleVal),
              role_name: roleNameVal
            }));
            if (formikRef.current && typeof formikRef.current.setFieldValue === 'function') {
              formikRef.current.setFieldValue('fullname', fullnameVal);
              formikRef.current.setFieldValue('username', usernameVal);
              formikRef.current.setFieldValue('password', passwordVal);
              formikRef.current.setFieldValue('email', emailVal);
              formikRef.current.setFieldValue('company', companyVal);
              formikRef.current.setFieldValue('company_name', companyNameVal);
              formikRef.current.setFieldValue('role', roleVal === null || roleVal === undefined ? '' : String(roleVal));
              formikRef.current.setFieldValue('role_name', roleNameVal);
            }
          }
        } catch (err) {
          console.error('Failed to fetch user:', err);
          onError && onError(t('notify.failedLoadData', 'Failed to load user data'));
        } finally {
          if (mounted) setLoading(false);
        }
      };
      fetch();
      return () => {
        mounted = false;
      };
    }, [id, onError, t]);

    const validationSchema = Yup.object().shape({
      fullname: Yup.string().nullable(),
      username: Yup.string().required(t('validation.usernameRequired', 'Username is required')),
      password: Yup.string().nullable(),
      company: Yup.mixed().nullable(),
      role: Yup.mixed().nullable()
    });

    const normalizeNumber = (v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = Number(String(v).replace(/,/g, ''));
      return Number.isFinite(n) ? n : null;
    };

    // --- 3. PAYLOAD BUILDER ---
    const buildPayload = (values) => {
      const payload = {
        fullname: values.fullname ?? '',
        username: values.username ?? '',
        password: values.password ?? '',
        email: values.email ?? '',
        locked: values.locked ?? false,
        company: normalizeNumber(values.company),
        role_name: values.role_name ?? ''
      };

      // Sisipkan ID hanya saat melakukan Edit
      if (id) {
        payload.id = id;
      }
      return payload;
    };

    const handleSubmit = async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSubmitStart();
      try {
        const payload = buildPayload(values);
        if (id) {
          await updateUser(id, payload);
          onSuccess && onSuccess(t('notify.updated', 'Updated successfully'));
        } else {
          await createUser(payload);
          onSuccess && onSuccess(t('notify.created', 'Created successfully'));
        }
        onClose && onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage =
          err?.response?.data?.message || err?.data?.message || err?.message || t('notify.failedSave', 'Failed to save data');
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
        onSubmitEnd();
      }
    };

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, handleBlur, setFieldValue, handleSubmit }) => {
            const resolvedRoleValue =
              (values.role_name ?? values.role_name ?? values.role_name) === '' ? '' : String(values.role_name ?? values.role_name ?? '');
            return (
              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'stretch', width: '100%' }}>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Stack spacing={2} sx={{ width: '100%' }}>
                      <TextField
                        label={t('general.fullName', 'Full Name')}
                        name="fullname"
                        value={values.fullname ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        sx={{ width: '100%' }}
                      />
                      <TextField
                        label={t('general.username', 'Username')}
                        name="username"
                        value={values.username ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        sx={{ width: '100%' }}
                        required
                      />
                      <TextField
                        label={t('general.password', 'Password')}
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={values.password ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        sx={{ width: '100%' }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showPassword ? t('general.hidePassword', 'Hide password') : t('general.showPassword', 'Show password')
                                }
                                onClick={() => setShowPassword((s) => !s)}
                                size="small"
                              >
                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      <TextField
                        label={t('general.email', 'Email')}
                        name="email"
                        value={values.email ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        sx={{ width: '100%' }}
                      />
                      <PickerField
                        label={t('general.company', 'Company')}
                        value={values.company ?? ''}
                        {...companyPickerParams}
                        options={companyOptions}
                        onChange={(opt) => {
                          const val = typeof opt === 'object' ? opt?.value : opt;
                          const selected = companyOptions.find((c) => String(c.value) === String(val)) ?? null;
                          const id = selected ? selected.value : (val ?? null);
                          setFieldValue('company', id);
                          setFieldValue('company_name', selected ? selected.label : '');
                          try {
                            if (id !== null && id !== undefined) localStorage.setItem('companyId', String(id));
                          } catch {}
                        }}
                        allowAdd={false}
                        dialogTitle={t('masterCompany.selectCompany', 'Select Company')}
                      />

                      {/* PENYESUAIAN PICKER FIELD ROLE UNTUK MENGIRIM TEXT PENCARIAN */}
                      <PickerField
                        label={t('general.role', 'Role')}
                        value={resolvedRoleValue}
                        {...rolePickerParams}
                        options={roleOptions}
                        onChange={(opt) => {
                          let sel = null;
                          if (opt && typeof opt === 'object' && opt.value !== undefined) {
                            sel = opt;
                          } else {
                            sel =
                              roleOptions.find((r) => String(r.value) === String(opt)) ??
                              roleOptions.find((r) => String(r.label) === String(opt));
                          }
                          if (sel) {
                            setFieldValue('role_name', sel.value);
                          } else {
                            setFieldValue('role_name', typeof opt === 'string' ? opt : '');
                          }
                        }}
                        allowAdd={false}
                      />
                    </Stack>
                  </Box>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>
    );
  }
);

export default MasterUserAddEdit;
